import debug from 'debug';
import Api from './Dictionary/Api';
import DictionaryStorage from './Dictionary/DictionaryStorage';

const log = debug('Dictionary');
const api = new Api();

/*
	Usage:
	--------------
	import DictionaryObject from 'Global/Shared/Components/Sitecore/DictionaryObject';

	this.dictionary = new DictionaryObject({
		dictionaryUrl: 'Forum_dk/ShoppingList/IncludeDescendants',
		includeDescendants: true (or false)
	})

	this.dictionary.Get().then( ... )

	this.dictionary.Text('headline') -- string is case insensitive
	this.dictionary.Text('component.list.headline')

	this.dictionary.Log() -- outputs dictionary JSON object to console
	this.dictionary.Log('component.list') -- outputs dictionary.component.list JSON object to console
*/

class DictionaryObject {
	/**
	 * @param {Object} settings
	 * @param {string} settings.dictionaryUrl
	 * @param {boolean} [settings.includeDescendants] default value is false
	 */
	constructor(settings) {
		if (!settings || !settings.dictionaryUrl) {
			log('Error: No settings given!');
			return;
		}

		this.dictionary = {};
		this.settings = settings;
		this.phrase = '';

		//this.sessionKey = this.settings.dictionaryUrl + this.settings.includeDescendants + this.Version();
		this.sessionKey = 'dictionary';
		this.loadingKey = 'dictionary-loading';
		this.cache = 3600000; // 60 minutes in ms

		this.dictionaryKey = this.DictionaryKey();
		this.dictionaryStorage = new DictionaryStorage({ componentKey: this.dictionaryKey });
	}

	/**
	 * @returns {string}
	 */
	DictionaryKey() {
		const pathArray = this.settings.dictionaryUrl.split('/');
		return pathArray[pathArray.length - 1];
	}

	/**
	 * @returns {Promise}
	 */
	Get() {
		return new Promise((resolve, reject) => {
			this.Load().then(dictionary => {
				
				if (dictionary !== null) {
					this.dictionary = dictionary;
					resolve();
					return;
				}

				api.Get({
					path: this.settings.dictionaryUrl,
					includeDescendants: this.settings.includeDescendants
				}).then(response => {
					this.dictionary = response;
					this.Save();
					resolve();
				}, error => {
					reject(error);
				});
			});
		});
	}

	/**
	 * @param {JSON} dictionary
	 * @param {string} key
	 * 
	 * @returns {string}
	 */
	GetParameterCaseInsensitive(dictionary, key) {
		return dictionary[Object.keys(dictionary)
			.find(k => k.toLowerCase() === key.toLowerCase())
		];
	}

	/**
	 * @returns {Promise}
	 */
	Load() {
		return new Promise((resolve, reject) => {

			const data = this.dictionaryStorage.load();

			if (data === null || data.dictionary === undefined) {
				this.dictionaryStorage.setItem(this.sessionKey, this.loadingKey);
				resolve(null);
				return;
			}

			if (data.dictionary === this.loadingKey) {
				log('Load() - waiting for event', this.dictionaryKey, this.loadingKey);
				document.addEventListener(this.loadingKey, event => {
					if (event.detail.dictionaryKey === this.dictionaryKey) {
						log(`Dictionary "${this.dictionaryKey}" from cache: `, event.detail.dictionary);
						resolve(event.detail.dictionary)
					} else {
						log('Load() - dictionaryKey does not match (key, component) ', event.detail.dictionaryKey, this.dictionaryKey);
					}
				});

				setTimeout(() => {
					const tempData = this.dictionaryStorage.load();

					if (tempData === null || tempData.dictionary === undefined) {
						this.dictionaryStorage.setItem(this.sessionKey, this.loadingKey);
						resolve(null);
						return;
					}

					if (tempData.dictionary === this.loadingKey) {
						log('Load() - waiting for event has expired', this.dictionaryKey, this.loadingKey);
						this.dictionaryStorage.setItem(this.sessionKey, this.loadingKey);
						resolve(null);
						return;
					}
				}, 1500);
			} else {
				if (data.expire < Date.now()) {
					log(`Dictionary "${this.dictionaryKey}" expired, cache ${this.cache/1000} seconds`);
					resolve(null);
					return;
				}

				log(`Dictionary "${this.dictionaryKey}" from storage: `, data.dictionary);
				resolve(data.dictionary);
			}
		});
	}

	/**
	 * @param {string} key key is a string - either in one level 'headline' - or as multiple levels 'component.list.title'. key is case insensitive
	 */
	Log(key) {
		if (key === undefined) {
			log('Log()', this.dictionary);
			return 'See Log() output';
		} 

		this.phrase = '';
		let phrase = this.GetParameterCaseInsensitive(this.dictionary, key);

		if (key.indexOf('.') > -1) {
			let keys = key.split('.');

			this.SetPhrase(this.dictionary, keys, 0);
			phrase = this.phrase;
		}

		log(`Log('${ key }')`, phrase);
		return 'See Log() output';
	}

	Save() {
		log(`Dictionary "${this.dictionaryKey}" from api: `, this.dictionary);

		const data = {
			dictionary: this.dictionary,
			expire: Date.now() + this.cache,
			includeDescendants: this.settings.includeDescendants,
			path: this.settings.dictionaryUrl
		}

		this.dictionaryStorage.save(data);
		// this.dictionaryStorage.setItem(this.sessionKey, JSON.stringify(data));

		const loadingEvent = new CustomEvent(this.loadingKey, {
			detail: {
				dictionary: this.dictionary,
				dictionaryKey: this.dictionaryKey
			}
		});
		document.dispatchEvent(loadingEvent);
	}

	/**
	 * @param {JSON} dictionary 
	 */
	Set(dictionary) {
		this.dictionary = dictionary;
	}

	/*
		Retrives the dictionary phrase with key: keys[index]
		If keys[index] returns an object, the functions is called again with the object as data source and with the next key in the keys array
	*/
	SetPhrase(dictionary, keys, index) {
		let phrase = this.GetParameterCaseInsensitive(dictionary, keys[index]);
		let newIndex = index + 1;
		if (typeof phrase === 'object' && newIndex < keys.length) {
			this.SetPhrase(phrase, keys, newIndex);
		} else {
			this.phrase = phrase;
		}
	}

	/*
		key is a string - either in one level 'headline' - or as multiple levels 'component.list.title'
		key is case insensitive
	*/
	Text(key) {
		this.phrase = '';
		let phrase = this.GetParameterCaseInsensitive(this.dictionary, key);

		if (key.indexOf('.') > -1) {
			let keys = key.split('.');

			this.SetPhrase(this.dictionary, keys, 0);
			phrase = this.phrase;
		}
		
		if (phrase !== undefined && typeof phrase !== 'object') {
			return phrase;
		}

		return `<span class="m-dictionary" data-dictionary-path="${ this.settings.dictionaryUrl }" title="Dictionary text is ${ typeof phrase }">${ key }</span>`
	}

	/**
	 * @returns {string}
	 */
	/*
	Version() {
		const now = new Date();
		return `-${ now.getDate() }-${ now.getMonth() + 1 }`;
	}
	*/
}

export default DictionaryObject;
